import React from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, CalendarIcon, Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import LocationDisplay from "./LocationDisplay";
import UserTimezoneDisplay from "./UserTimezoneDisplay";
import { format } from "date-fns";

interface QuickFiltersBarProps {
  // Distance filter props
  distance: string;
  onDistanceChange: (value: string) => void;
  
  // Date/Time filter props
  date: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  timeSlot: string;
  onTimeSlotChange: (timeSlot: string) => void;
  timeSlots: string[];
  isDateDisabled: (date: Date) => boolean;
  
  // Location props
  userLocation: {lat: number, lng: number} | null;
  locationError: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  isGettingLocation: boolean;
  isLoadingAddress: boolean;
  timezoneDisplay: string | null;
  isApproximateLocation?: boolean;
  requestLocationAccess: () => void;
}

const QuickFiltersBar: React.FC<QuickFiltersBarProps> = ({
  distance,
  onDistanceChange,
  date,
  onDateChange,
  timeSlot,
  onTimeSlotChange,
  timeSlots,
  isDateDisabled,
  userLocation,
  locationError,
  city,
  state,
  country,
  isGettingLocation,
  isLoadingAddress,
  timezoneDisplay,
  isApproximateLocation,
  requestLocationAccess,
}) => {
  const { t } = useTranslation('practitioners');
  const { t: tFilters } = useTranslation('filters');

  const getTimeSlotDisplayName = (slot: string) => {
    if (slot === 'any_time') return tFilters('time.anyTime');
    return slot;
  };

  return (
    <div className="mb-6 space-y-4">
      {/* Location and Timezone Cards - Horizontal */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(userLocation || isGettingLocation || isLoadingAddress || locationError) && (
          <LocationDisplay
            city={city}
            state={state}
            country={country}
            isLoading={isLoadingAddress}
            hasError={!!locationError}
            isApproximate={isApproximateLocation}
            onRefresh={requestLocationAccess}
          />
        )}
        
        <UserTimezoneDisplay
          timezoneDisplay={timezoneDisplay}
          onRefresh={requestLocationAccess}
        />
      </div>

      {/* Distance, Date, Time Filters - Horizontal */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Distance Filter */}
        <div>
          <label className="text-sm font-medium text-foreground mb-1.5 flex items-center">
            <MapPin className="mr-1.5 h-4 w-4 text-amber-600" /> {t('distance')}
          </label>
          <Select value={distance} onValueChange={onDistanceChange}>
            <SelectTrigger className="border-amber-200">
              <SelectValue placeholder={t('anyDistance')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any_distance">{t('anyDistance')}</SelectItem>
              <SelectItem value="5">{t('within5Miles')}</SelectItem>
              <SelectItem value="10">{t('within10Miles')}</SelectItem>
              <SelectItem value="25">{t('within25Miles')}</SelectItem>
              <SelectItem value="50">{t('within50Miles')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Date Filter */}
        <div>
          <label className="text-sm font-medium text-foreground mb-1.5 flex items-center">
            <CalendarIcon className="mr-1.5 h-4 w-4 text-amber-600" /> {tFilters('date.label')}
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={`w-full border-amber-200 justify-start text-left font-normal ${
                  !date && "text-muted-foreground"
                }`}
              >
                {date ? format(date, "PPP") : tFilters('date.selectDate')}
                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={onDateChange}
                disabled={isDateDisabled}
                initialFocus
                className="p-3"
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Time Filter */}
        <div>
          <label className="text-sm font-medium text-foreground mb-1.5 flex items-center">
            <Clock className="mr-1.5 h-4 w-4 text-amber-600" /> {tFilters('time.label')}
          </label>
          <Select value={timeSlot} onValueChange={onTimeSlotChange}>
            <SelectTrigger className="border-amber-200">
              <SelectValue placeholder={tFilters('time.selectTime')} />
            </SelectTrigger>
            <SelectContent>
              {timeSlots.map((slot) => (
                <SelectItem key={slot} value={slot}>
                  {getTimeSlotDisplayName(slot)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default QuickFiltersBar;
